// US-101662 Scenario 2: built-in vector shapes that participate in full
// Fill/Outline configuration. The `key` is what gets persisted into
// `selectedIcon` so Cesium can resolve the same value at render time.
// Kept as inline SVG paths so the panel renders identically regardless of
// what's in /public/assets — and so we never block on a network round-trip.

export interface BuiltInShape {
  key: string;
  label: string;
  // Inline SVG path drawn inside a 0 0 24 24 viewBox. `currentColor` lets the
  // grid use the user's chosen fill for preview.
  path: string;
}

export const BUILT_IN_SHAPES: BuiltInShape[] = [
  {
    key: 'shape:circle',
    label: 'Circle',
    path: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16z',
  },
  {
    key: 'shape:star',
    label: 'Star',
    path: 'M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6.1L12 16.9l-5.4 2.7 1.2-6.1L3.3 9.3l6.1-.7L12 3z',
  },
  {
    key: 'shape:square',
    label: 'Square',
    path: 'M5 5h14v14H5z',
  },
  {
    key: 'shape:cross',
    label: 'Cross',
    path: 'M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7V4z',
  },
  {
    key: 'shape:triangle',
    label: 'Triangle',
    path: 'M12 4l9 16H3z',
  },
  {
    key: 'shape:burst',
    label: 'Burst',
    path: 'M12 2l2 3.5 4-1-1 4 3.5 2-3.5 2 1 4-4-1-2 3.5-2-3.5-4 1 1-4L2.5 10 6 8 5 4l4 1z',
  },
  {
    key: 'shape:diamond',
    label: 'Diamond',
    path: 'M12 3l8 9-8 9-8-9z',
  },
];

export const isBuiltInShape = (key?: string | null): boolean => !!key && key.startsWith('shape:');

export const findBuiltInShape = (key?: string | null): BuiltInShape | undefined => BUILT_IN_SHAPES.find((s) => s.key === key);

// GeoServer SLD spec only recognises 6 standard WellKnownNames out of the
// box: circle, square, triangle, star, cross, x. The two non-standard ones
// in our picker are mapped to the closest visual match. A diamond has no
// native mark — it's a square rotated 45°, so we emit `square` plus a 45°
// rotation offset. Anything starting with `shape:` is a built-in; everything
// else is treated as a raw WKN or an image key.
export const shapeKeyToMark = (key?: string | null): { wellKnownName: string; rotationOffset: number } => {
  if (!key || !key.startsWith('shape:')) return { wellKnownName: key || 'circle', rotationOffset: 0 };
  const stripped = key.slice('shape:'.length);
  switch (stripped) {
    case 'circle':
    case 'square':
    case 'triangle':
    case 'star':
    case 'cross':
    case 'x':
      return { wellKnownName: stripped, rotationOffset: 0 };
    case 'burst':
      return { wellKnownName: 'star', rotationOffset: 0 };
    case 'diamond':
      // A diamond is a square turned 45°.
      return { wellKnownName: 'square', rotationOffset: 45 };
    default:
      return { wellKnownName: 'circle', rotationOffset: 0 };
  }
};

export const shapeKeyToWellKnownName = (key?: string | null): string => shapeKeyToMark(key).wellKnownName;
